import { useEffect, useRef } from 'react';
import { useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * FreehandSelector component for freehand polygon drawing on the map
 * @param {boolean} enabled - Whether freehand selection is enabled
 * @param {Function} onSelectionComplete - Callback when selection is complete (receives polygon: L.Polygon)
 */
const FreehandSelector = ({ enabled, onSelectionComplete }) => {
  const map = useMap();
  const isDrawingRef = useRef(false);
  const polygonRef = useRef(null);
  const pointsRef = useRef([]);

  // Use useMapEvents for proper event handling
  useMapEvents({
    mousedown: (e) => {
      if (!enabled) return;
      
      // Only start on left click
      if (e.originalEvent.button !== 0) return;
      
      isDrawingRef.current = true;
      const point = e.latlng;
      pointsRef.current = [[point.lat, point.lng]];

      // Create polygon element
      if (polygonRef.current) {
        map.removeLayer(polygonRef.current);
      }
      
      polygonRef.current = L.polygon([pointsRef.current], {
        color: '#2196f3',
        fillColor: '#2196f3',
        fillOpacity: 0.2,
        weight: 2,
        dashArray: '5, 5',
        interactive: false,
      });
      polygonRef.current.addTo(map);

      // Prevent map drag during drawing
      map.dragging.disable();
      map.doubleClickZoom.disable();
    },

    mousemove: (e) => {
      if (!enabled || !isDrawingRef.current || !polygonRef.current) return;

      const point = e.latlng;
      // Add point to polygon
      pointsRef.current.push([point.lat, point.lng]);
      polygonRef.current.setLatLngs(pointsRef.current);
    },

    mouseup: (e) => {
      if (!enabled || !isDrawingRef.current || !polygonRef.current) return;

      // Finish drawing on mouseup
      const polygon = polygonRef.current;
      
      // Only finish if we have at least 3 points (minimum for a polygon)
      if (pointsRef.current.length >= 3) {
        // Close the polygon by adding the first point at the end
        const finalPoints = [...pointsRef.current];
        if (finalPoints.length > 0) {
          const firstPoint = finalPoints[0];
          const lastPoint = finalPoints[finalPoints.length - 1];
          // Only add closing point if it's not already close to the first point
          const distance = Math.sqrt(
            Math.pow(firstPoint[0] - lastPoint[0], 2) + 
            Math.pow(firstPoint[1] - lastPoint[1], 2)
          );
          if (distance > 0.0001) { // Add first point to close polygon
            finalPoints.push([firstPoint[0], firstPoint[1]]);
          }
        }
        
        map.removeLayer(polygon);
        polygonRef.current = null;
        isDrawingRef.current = false;
        pointsRef.current = [];

        map.dragging.enable();
        map.doubleClickZoom.enable();

        if (onSelectionComplete) {
          // Create a new polygon with the final points to ensure it's valid
          const finalPolygon = L.polygon(finalPoints);
          onSelectionComplete(finalPolygon);
        }
      } else {
        // Not enough points - clear the polygon from screen
        map.removeLayer(polygon);
        polygonRef.current = null;
        isDrawingRef.current = false;
        pointsRef.current = [];
        
        map.dragging.enable();
        map.doubleClickZoom.enable();
      }
    },

    mouseleave: () => {
      if (!enabled) return;
      // Don't finish on mouse leave - allow crossing map boundaries
    }
  });

  useEffect(() => {
    if (!enabled) {
      if (polygonRef.current) {
        map.removeLayer(polygonRef.current);
        polygonRef.current = null;
      }
      isDrawingRef.current = false;
      pointsRef.current = [];
      map.dragging.enable();
      map.doubleClickZoom.enable();
    }
  }, [enabled, map]);

  return null;
};

export default FreehandSelector;

